import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** What the change will do — to whom, and what stays as it was. */
  description: ReactNode
  confirmLabel: string
  destructive?: boolean | undefined
  busy?: boolean | undefined
  /** Not yet: what the description depends on is still being checked, or could not be. */
  confirmDisabled?: boolean | undefined
  error?: string | null | undefined
  onConfirm: () => void
}

/**
 * A plain yes-or-no for a change that reaches other people. The description is
 * where the work is: say what happens and to whom, not "are you sure?".
 */
export function ConfirmDialog(props: ConfirmDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
          <DialogDescription>{props.description}</DialogDescription>
        </DialogHeader>
        {props.error ? <p className="text-sm text-danger-ink">{props.error}</p> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={props.destructive ? 'destructive' : 'default'}
            pending={props.busy}
            disabled={props.confirmDisabled}
            onClick={props.onConfirm}
          >
            {props.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
